import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../../components/charts';
import { Loader2, RefreshCw } from 'lucide-react';

export default function AdjustTraffic() {
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('7');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [dataRows, setDataRows] = useState([]);
  const [metricToggle, setMetricToggle] = useState('sessions'); // sessions | installs | purchases | revenue
  const [pieMetric, setPieMetric] = useState('sessions');

  const hasData = Array.isArray(dataRows) && dataRows.length > 0;

  const toYmdLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const palette = ['#111827', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#10b981'];

  const trend = useMemo(() => {
    if (!hasData) return { labels: [], sessions: [], installs: [], purchases: [], revenue: [] };
    const map = new Map();
    dataRows.forEach(r => {
      const raw = r.day || r.date;
      if (!raw) return;
      const key = String(raw).slice(0, 10); // YYYY-MM-DD only (no time)
      const cur = map.get(key) || { sessions: 0, installs: 0, purchases: 0, revenue: 0 };
      cur.sessions += Number(r.sessions || 0);
      cur.installs += Number(r.installs || 0);
      cur.purchases += Number(r.purchase_events || r.events || 0);
      cur.revenue += Number(r.all_revenue || 0);
      map.set(key, cur);
    });
    const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return {
      labels: entries.map(e => e[0]),
      sessions: entries.map(e => e[1].sessions),
      installs: entries.map(e => e[1].installs),
      purchases: entries.map(e => e[1].purchases),
      revenue: entries.map(e => e[1].revenue)
    };
  }, [dataRows, hasData]);

  const totals = useMemo(() => {
    if (!hasData) return { sessions: 0, installs: 0, purchases: 0, revenue: 0 };
    return dataRows.reduce((acc, r) => {
      acc.sessions += Number(r.sessions || 0);
      acc.installs += Number(r.installs || 0);
      acc.purchases += Number(r.purchase_events || r.events || 0);
      acc.revenue += Number((r.all_revenue ?? r.revenue) || 0);
      return acc;
    }, { sessions: 0, installs: 0, purchases: 0, revenue: 0 });
  }, [dataRows, hasData]);

  const aggregateBy = (rows, dimension, metric) => {
    const getVal = (row) => {
      switch (metric) {
        case 'sessions': return Number(row.sessions || 0);
        case 'installs': return Number(row.installs || 0);
        case 'purchases': return Number(row.purchase_events || row.events || 0);
        case 'revenue': return Number(row.all_revenue || 0);
        default: return 0;
      }
    };
    const keyOf = (row) => {
      switch (dimension) {
        case 'channel': return row.channel || row.source_network || 'Unknown';
        case 'os': return row.os_name || 'Unknown';
        case 'partner': return row.partner || 'Unknown';
        default: return 'Unknown';
      }
    };
    const map = new Map();
    rows.forEach(r => {
      const k = keyOf(r);
      map.set(k, (map.get(k) || 0) + getVal(r));
    });
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    return sorted;
  };

  const channelAgg = useMemo(() => {
    if (!hasData) return [];
    return aggregateBy(dataRows, 'channel', pieMetric);
  }, [dataRows, hasData, pieMetric]);

  const osAgg = useMemo(() => {
    if (!hasData) return [];
    return aggregateBy(dataRows, 'os', 'sessions');
  }, [dataRows, hasData]);

  const topChannelsTable = useMemo(() => {
    if (!hasData) return [];
    // Aggregate by channel across metrics
    const map = new Map();
    dataRows.forEach(r => {
      const ch = r.channel || r.source_network || 'Unknown';
      const cur = map.get(ch) || { sessions: 0, installs: 0, purchases: 0, revenue: 0 };
      cur.sessions += Number(r.sessions || 0);
      cur.installs += Number(r.installs || 0);
      cur.purchases += Number(r.purchase_events || r.events || 0);
      cur.revenue += Number((r.all_revenue ?? r.revenue) || 0);
      map.set(ch, cur);
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 12);
  }, [dataRows, hasData]);

  const topPartnersTable = useMemo(() => {
    if (!hasData) return [];
    const map = new Map();
    dataRows.forEach(r => {
      const p = r.partner || 'Unknown';
      const cur = map.get(p) || { sessions: 0, installs: 0, purchases: 0, revenue: 0 };
      cur.sessions += Number(r.sessions || 0);
      cur.installs += Number(r.installs || 0);
      cur.purchases += Number(r.purchase_events || r.events || 0);
      cur.revenue += Number((r.all_revenue ?? r.revenue) || 0);
      map.set(p, cur);
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 12);
  }, [dataRows, hasData]);

  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const targetBrandId = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      const dateRange = selectedDateRange === 'today' ? 'today'
        : selectedDateRange === 'yesterday' ? 'yesterday'
        : selectedDateRange === '7' ? 'last7'
        : selectedDateRange === '30' ? 'last30'
        : selectedDateRange === 'thisMonth' ? 'thisMonth'
        : selectedDateRange === 'lastMonth' ? 'lastMonth'
        : 'custom';

      let startYmd, endYmd;
      const now = new Date();
      if (dateRange === 'today') {
        startYmd = toYmdLocal(now);
        endYmd = toYmdLocal(now);
      } else if (dateRange === 'yesterday') {
        const d = new Date(); d.setDate(d.getDate() - 1);
        startYmd = toYmdLocal(d);
        endYmd = toYmdLocal(d);
      } else if (dateRange === 'last7') {
        const d = new Date(); d.setDate(d.getDate() - 7);
        startYmd = toYmdLocal(d);
        endYmd = toYmdLocal(now);
      } else if (dateRange === 'last30') {
        const d = new Date(); d.setDate(d.getDate() - 30);
        startYmd = toYmdLocal(d);
        endYmd = toYmdLocal(now);
      } else if (dateRange === 'thisMonth') {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        startYmd = toYmdLocal(d);
        endYmd = toYmdLocal(now);
      } else if (dateRange === 'lastMonth') {
        const d1 = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const d2 = new Date(now.getFullYear(), now.getMonth(), 0);
        startYmd = toYmdLocal(d1);
        endYmd = toYmdLocal(d2);
      } else if (customStartDate && customEndDate) {
        startYmd = toYmdLocal(customStartDate);
        endYmd = toYmdLocal(customEndDate);
      } else {
        const d = new Date(); d.setDate(d.getDate() - 7);
        startYmd = toYmdLocal(d);
        endYmd = toYmdLocal(now);
      }

      // Dummy data mode for specific brand
      if (brandId === targetBrandId) {
        let rangeStart = new Date(`${startYmd}T00:00:00`);
        let rangeEnd = new Date(`${endYmd}T00:00:00`);
        if (rangeStart > rangeEnd) {
          const tmp = rangeStart; rangeStart = rangeEnd; rangeEnd = tmp;
        }
        const days = [];
        const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
        const endDate = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
        while (cursor <= endDate) {
          days.push(new Date(cursor));
          cursor.setDate(cursor.getDate() + 1);
        }
        const syntheticRows = [];
        days.forEach((d, idx) => {
          const dayStr = toYmdLocal(d);
          const baseSessions = 1200 + (idx % 7) * 150 + Math.round(Math.sin(idx / 3) * 80);
          const installs = Math.round(baseSessions * 0.45);
          const purchaseRate = 0.07 + ((idx % 5) * 0.004);
          const purchases = Math.round(baseSessions * purchaseRate);
          const avgOrder = 900 + (idx % 4) * 120;
          const revenue = purchases * avgOrder;
          const rowsForDay = [
            {
              channel: 'Facebook Ads',
              source_network: 'Facebook',
              campaign: 'Prospecting - Broad',
              os_name: 'iOS',
              platform: 'iOS App',
              partner: 'Partner A',
              sessions: Math.round(baseSessions * 0.5),
              purchase_events: Math.round(purchases * 0.5),
              installs: Math.round(installs * 0.5),
              all_revenue: Math.round(revenue * 0.5),
              day: dayStr,
            },
            {
              channel: 'Google Ads',
              source_network: 'Google',
              campaign: 'Performance Max',
              os_name: 'Android',
              platform: 'Android App',
              partner: 'Partner B',
              sessions: Math.round(baseSessions * 0.3),
              purchase_events: Math.round(purchases * 0.3),
              installs: Math.round(installs * 0.3),
              all_revenue: Math.round(revenue * 0.3),
              day: dayStr,
            },
            {
              channel: 'Apple Search Ads',
              source_network: 'Apple Search Ads',
              campaign: 'Brand - Exact',
              os_name: 'iOS',
              platform: 'iOS App',
              partner: 'Partner A',
              sessions: Math.round(baseSessions * 0.2),
              purchase_events: Math.round(purchases * 0.2),
              installs: Math.round(installs * 0.2),
              all_revenue: Math.round(revenue * 0.2),
              day: dayStr,
            },
          ];
          rowsForDay.forEach(r => syntheticRows.push(r));
        });
        setDataRows(syntheticRows);
        return;
      }

      const params = new URLSearchParams({
        startDate: startYmd,
        endDate: endYmd,
        limit: '50000'
      });
      const res = await fetch(`https://api.glowytics.com/api/adjust/performance?${params.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId },
      });
      const ct = res.headers.get('content-type') || '';
      const js = ct.includes('application/json') ? await res.json() : {};
      const rows = js && js.ok && Array.isArray(js.rows) ? js.rows : (Array.isArray(js.rows) ? js.rows : (Array.isArray(js.data) ? js.data : []));
      setDataRows(rows || []);
    } catch (e) {
      console.error('Adjust traffic fetch error:', e);
      setDataRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
              <img
                src="/adjustlogopng.png"
                alt="Adjust"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Adjust • Traffic Acquisition</h1>
              <p className="text-sm text-gray-500 mt-1">Channels, OS and partners performance</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
              value={selectedDateRange}
              onChange={(e) => {
                setSelectedDateRange(e.target.value);
                if (e.target.value === 'custom') {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                }
              }}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom Range</option>
            </select>
            {selectedDateRange === 'custom' && (
              <div className="relative">
                <button
                  onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  {customStartDate && customEndDate ? (
                    `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                  ) : (
                    'Select Date Range'
                  )}
                </button>
                {isCustomDateOpen && (
                  <div className="absolute right-0 mt-2 z-50">
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                      <DatePicker
                        selected={customStartDate}
                        onChange={handleCustomDateChange}
                        startDate={customStartDate}
                        endDate={customEndDate}
                        selectsRange
                        inline
                        calendarClassName="bg-white"
                        monthClassName="text-gray-800"
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setIsCustomDateOpen(false);
                            fetchData();
                          }}
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              className="p-2 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-60"
              onClick={() => fetchData()}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 rounded-xl">
          <p className="text-gray-500 text-sm mb-1">Sessions</p>
          <p className="text-2xl font-bold">
            {loading ? <span className="inline-block w-24 h-8 bg-gray-100 animate-pulse rounded"></span>
              : Number(totals.sessions).toLocaleString('en-US')}
          </p>
        </Card>
        <Card className="p-4 rounded-xl">
          <p className="text-gray-500 text-sm mb-1">Installs</p>
          <p className="text-2xl font-bold">
            {loading ? <span className="inline-block w-24 h-8 bg-gray-100 animate-pulse rounded"></span>
              : Number(totals.installs).toLocaleString('en-US')}
          </p>
        </Card>
        <Card className="p-4 rounded-xl">
          <p className="text-gray-500 text-sm mb-1">Purchases</p>
          <p className="text-2xl font-bold">
            {loading ? <span className="inline-block w-24 h-8 bg-gray-100 animate-pulse rounded"></span>
              : Number(totals.purchases).toLocaleString('en-US')}
          </p>
        </Card>
        <Card className="p-4 rounded-xl">
          <p className="text-gray-500 text-sm mb-1">Revenue</p>
          <p className="text-2xl font-bold">
            {loading ? <span className="inline-block w-24 h-8 bg-gray-100 animate-pulse rounded"></span>
              : '₺' + Number(totals.revenue).toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </p>
        </Card>
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Sessions Trend</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <LineChart
              data={{
                labels: trend.labels,
                datasets: [
                  {
                    label: 'Sessions',
                    data: trend.sessions,
                    borderColor: '#111827',
                    backgroundColor: 'rgba(17,24,39,0.12)',
                    fill: true,
                  },
                ],
              }}
            />
          )}
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Installs vs Purchases</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <BarChart
              data={{
                labels: trend.labels,
                datasets: [
                  {
                    label: 'Installs',
                    data: trend.installs,
                    backgroundColor: 'rgba(99,102,241,0.5)',
                    borderColor: 'rgba(99,102,241,1)',
                  },
                  {
                    label: 'Purchases',
                    data: trend.purchases,
                    backgroundColor: 'rgba(16,185,129,0.5)',
                    borderColor: 'rgba(16,185,129,1)',
                  },
                ],
              }}
            />
          )}
        </Card>
      </div>

      {/* Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Channel Share</h3>
            <select
              className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white"
              value={pieMetric}
              onChange={(e) => setPieMetric(e.target.value)}
            >
              <option value="sessions">Sessions</option>
              <option value="revenue">Revenue</option>
              <option value="installs">Installs</option>
              <option value="purchases">Purchases</option>
            </select>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : channelAgg.length === 0 ? (
            <div className="text-sm text-gray-500 py-12 text-center">No data</div>
          ) : (
            <DoughnutChart
              data={{
                labels: channelAgg.slice(0, 6).map(([k]) => k),
                datasets: [
                  {
                    data: channelAgg.slice(0, 6).map(([, v]) => v),
                    backgroundColor: channelAgg.slice(0, 6).map((_, i) => palette[i % palette.length]),
                  },
                ],
              }}
            />
          )}
        </Card>
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">OS Share</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : osAgg.length === 0 ? (
            <div className="text-sm text-gray-500 py-12 text-center">No data</div>
          ) : (
            <DoughnutChart
              data={{
                labels: osAgg.map(([k]) => k),
                datasets: [
                  {
                    data: osAgg.map(([, v]) => v),
                    backgroundColor: osAgg.map((_, i) => palette[i % palette.length]),
                  },
                ],
              }}
            />
          )}
        </Card>
        <Card className="p-4 rounded-2xl lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Top Channels</h3>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">Sort by</span>
              <select
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white"
                value={metricToggle}
                onChange={(e) => setMetricToggle(e.target.value)}
              >
                <option value="sessions">Sessions</option>
                <option value="installs">Installs</option>
                <option value="purchases">Purchases</option>
                <option value="revenue">Revenue</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Channel</th>
                    <th className="text-right p-2">Sessions</th>
                    <th className="text-right p-2">Installs</th>
                    <th className="text-right p-2">Purchases</th>
                    <th className="text-right p-2">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topChannelsTable
                    .sort((a, b) => {
                      if (metricToggle === 'sessions') return b.sessions - a.sessions;
                      if (metricToggle === 'installs') return b.installs - a.installs;
                      if (metricToggle === 'purchases') return b.purchases - a.purchases;
                      return b.revenue - a.revenue;
                    })
                    .map((row) => (
                      <tr key={row.name} className="hover:bg-gray-50">
                        <td className="p-2 text-gray-800">{row.name}</td>
                        <td className="p-2 text-right">{Number(row.sessions).toLocaleString('en-US')}</td>
                        <td className="p-2 text-right">{Number(row.installs).toLocaleString('en-US')}</td>
                        <td className="p-2 text-right">{Number(row.purchases).toLocaleString('en-US')}</td>
                        <td className="p-2 text-right">₺{Number(row.revenue).toLocaleString('en-US')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
      
      {/* Top Partners */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Top Partners</h3>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">Sort by</span>
              <select
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white"
                value={metricToggle}
                onChange={(e) => setMetricToggle(e.target.value)}
              >
                <option value="sessions">Sessions</option>
                <option value="installs">Installs</option>
                <option value="purchases">Purchases</option>
                <option value="revenue">Revenue</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : topPartnersTable.length === 0 ? (
              <div className="text-sm text-gray-500 py-12 text-center">No data</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Partner</th>
                    <th className="text-right p-2">Sessions</th>
                    <th className="text-right p-2">Installs</th>
                    <th className="text-right p-2">Purchases</th>
                    <th className="text-right p-2">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topPartnersTable
                    .sort((a, b) => {
                      if (metricToggle === 'sessions') return b.sessions - a.sessions;
                      if (metricToggle === 'installs') return b.installs - a.installs;
                      if (metricToggle === 'purchases') return b.purchases - a.purchases;
                      return b.revenue - a.revenue;
                    })
                    .map((row) => (
                      <tr key={row.name} className="hover:bg-gray-50">
                        <td className="p-2 text-gray-800">{row.name}</td>
                        <td className="p-2 text-right">{Number(row.sessions).toLocaleString('en-US')}</td>
                        <td className="p-2 text-right">{Number(row.installs).toLocaleString('en-US')}</td>
                        <td className="p-2 text-right">{Number(row.purchases).toLocaleString('en-US')}</td>
                        <td className="p-2 text-right">₺{Number(row.revenue).toLocaleString('en-US')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}


